import { createRootRoute, Outlet } from '@tanstack/react-router';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  init,
  backButton,
  miniApp,
  swipeBehavior,
  locationManager,
  closingBehavior,
  viewport,
  targetOrigin,
} from '@telegram-apps/sdk-react';
import moment from 'moment';
import { useEffect } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { SWRConfig } from 'swr';

import { appMode } from '@app/config';
import { logger } from '@app/logger';

import { AuthProvider } from '@common/providers/AuthProvider';
import { NotificationsProvider } from '@common/providers/NotificationsProvider';
import { TabbarProvider } from '@common/providers/TabbarProvider';
import { Button } from '@common/ui/buttons/Button';
import { Result } from '@common/ui/Result';
import { resolveLauncherTargetOrigin } from '@common/utils/launcherTargetOrigin';
import { getStartParam } from '@common/utils/startParam';

import { SseEngineContextProvider } from '@services/SSE';

export const Route = createRootRoute({
  component: Component,
  notFoundComponent: () => <div>Not found</div>,
  errorComponent: RootErrorComponent,
});

/**
 * Rendered by TanStack Router when a route throws.
 *
 * Pure UI on purpose — reporting happens in `defaultOnCatch` on the router
 * (see main.tsx), which fires once per caught error from componentDidCatch,
 * receives the React componentStack (unavailable here), and sends the crash
 * both to our own backend and to Sentry with the real error as the primary
 * object. Anything captured from this component historically re-fired on
 * re-renders and flooded the quota; keep it render-only.
 */
function RootErrorComponent({ error }: { error: Error }) {
  // On the staging build the app is intentionally being reworked, so any
  // failure that bubbles up is almost certainly "the cutover broke
  // something we expected". Don't show users a generic "что-то пошло не
  // так" — tell them it's maintenance. In production builds this branch
  // is dead code (Vite drops it) and the regular error UI is shown.
  if (appMode === 'staging') {
    return (
      <Result
        src={'/icons/3d-icons/sand-clock.png'}
        title="Идут технические работы"
        description="Приложение временно недоступно. Загляните чуть позже."
        button={
          <Button
            testId="root-error-reload-button"
            onClick={() => {
              window.location.reload();
            }}
          >
            Обновить страницу
          </Button>
        }
      />
    );
  }

  return (
    <Result
      src={'/icons/3d-icons/sad.png'}
      title="Что-то пошло не так"
      description={getErrorMessage(error)}
      button={
        <Button
          testId="root-error-reload-button"
          onClick={() => {
            window.location.reload();
          }}
        >
          Обновить страницу
        </Button>
      }
    />
  );
}
//вынести куда то вместе с errorComponent
function getErrorMessage(error: Error) {
  if (error.message === 'LaunchParamsRetrieveError') {
    return 'Откройте приложение в Telegram, или обновите версию';
  }
  return 'Пожалуйста, попробуйте позже';
}

export default function Component() {
  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;

    const showRoot = () => root.classList.add('visible');

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', showRoot);
    } else {
      showRoot();
    }

    return () => {
      document.removeEventListener('DOMContentLoaded', showRoot);
    };
  }, []);

  useEffect(() => {
    (async () => {
      // Helper: run an SDK call, swallow + capture any FunctionNotAvailable /
      // unsupported-version errors so one failing call doesn't cascade and
      // skip later setup steps. Calls are independent — `setHeaderColor`
      // throwing must not skip `setBackgroundColor`, etc.
      const safe = async (label: string, fn: () => unknown) => {
        try {
          await fn();
        } catch (sdkError) {
          logger.captureException(sdkError, {
            tags: { sdkInit: label },
          });
        }
      };

      // When one of our launcher shells frames the app (the iOS fix — see
      // launcherTargetOrigin.ts for why), the SDK's postMessage transport must
      // target THAT origin or the browser drops every event. Must precede
      // init(): the mounts below post immediately.
      const launcherOrigin = resolveLauncherTargetOrigin(
        window.self !== window.top,
        window.location.ancestorOrigins,
      );
      if (launcherOrigin) {
        await safe('targetOrigin.set', () => targetOrigin.set(launcherOrigin));
      }

      await safe('init', () => init());

      // Always-supported components (Mini Apps v6.x baseline) — no gate.
      await safe('backButton.mount', () => backButton.mount());
      await safe('miniApp.mount', () => miniApp.mount());
      await safe('viewport.mount', () => viewport.mount());
      await safe('closingBehavior.mount', () => closingBehavior.mount());

      // Capability-gated mounts. Optional-chaining (`?.`) does NOT help
      // here: the SDK exports always exist; the underlying methods throw
      // FunctionNotAvailableError synchronously when the user's Telegram
      // client predates the feature. Without `.isAvailable()` checks the
      // throws bubble out of this async IIFE as `unhandledrejection`
      // events. Sentry issues:
      //   #52716543 / #117970029 — swipeBehavior.mount  (since v7.7)
      //   #52716086              — locationManager.mount (since v8.0)
      // (Predominantly Android Telegram clients lingering on older
      // Mini Apps versions.)
      if (swipeBehavior.mount.isAvailable()) {
        await safe('swipeBehavior.mount', () => swipeBehavior.mount());
      }
      if (locationManager.mount.isAvailable()) {
        await safe('locationManager.mount', () => locationManager.mount());
      }

      // setHeaderColor: `.isAvailable()` only confirms the function exists.
      // Accepting an RGB hex argument is itself version-gated (Sentry
      // #118237086 — "option rgb is not supported in Mini Apps version 6.7").
      // Use the SDK's documented sub-check `supports.rgb()`; fall back to
      // the theme key 'bg_color' on older clients so we still get a header
      // color (just the Telegram theme default rather than our custom hex).
      if (miniApp.setHeaderColor.isAvailable()) {
        await safe('miniApp.setHeaderColor', () => {
          if (miniApp.setHeaderColor.supports.rgb()) {
            miniApp.setHeaderColor('#f7f9fc');
          } else {
            miniApp.setHeaderColor('bg_color');
          }
        });
      }
      // setBackgroundColor has no granular `supports.rgb()` per the SDK
      // typings — wrap defensively so any sub-version throw doesn't take
      // the rest of the init down.
      if (miniApp.setBackgroundColor.isAvailable()) {
        await safe('miniApp.setBackgroundColor', () =>
          miniApp.setBackgroundColor('#f7f9fc'),
        );
      }
      if (swipeBehavior.disableVertical.isAvailable()) {
        await safe('swipeBehavior.disableVertical', () =>
          swipeBehavior.disableVertical(),
        );
      }
      if (closingBehavior.enableConfirmation.isAvailable()) {
        await safe('closingBehavior.enableConfirmation', () =>
          closingBehavior.enableConfirmation(),
        );
      }

      // referral capture via Telegram startParam
      try {
        const raw = getStartParam();

        // Capture referral code from startParam and send to Metrica; persist first referral code only
        const firstStored =
          typeof localStorage !== 'undefined'
            ? localStorage.getItem('referral_code_first')
            : null;
        const isRef = typeof raw === 'string' && raw.startsWith('r_');
        const isToken = typeof raw === 'string' && raw.startsWith('u_');
        const token = isToken ? raw.slice(2) : null;

        let referral = 'organic';
        if (isRef) {
          const code = raw.slice(2);
          if (code && code.length > 0) referral = code;
        } else if (!raw && firstStored) {
          referral = firstStored;
        }
        try {
          // Only real referral codes should be stored; tokens/campaigns are handled by server
          sessionStorage.setItem('referral_code', referral);
          if (isRef && !firstStored) {
            const code = raw.slice(2);
            if (code && code.length > 0) {
              localStorage.setItem('referral_code_first', code);
            }
          }
        } catch (_e) {
          // ignore storage errors
          void 0;
        }
        try {
          // If a token is present in start_param, use it as YM userID for cross-surface stitching
          const wy = window as unknown as {
            ym?: (id: number, action: string, ...rest: unknown[]) => void;
          };
          if (isToken && token && wy.ym) {
            wy.ym(100644700, 'setUserID', token);
          }
        } catch (_e) {
          void 0;
        }

        try {
          (
            window as unknown as {
              ym?: (id: number, action: string, ...rest: unknown[]) => void;
            }
          ).ym?.(100644700, 'params', {
            referral_code: referral,
            source: 'tma',
          });
        } catch (_e) {
          // ignore metrica errors
          void 0;
        }
        // Deduplicate by Telegram user id
        try {
          const w = window as unknown as {
            Telegram?: {
              WebApp?: {
                initDataUnsafe?: {
                  user?: { id?: number | string };
                };
              };
            };
            ym?: (id: number, action: string, ...rest: unknown[]) => void;
          };
          const uid = w?.Telegram?.WebApp?.initDataUnsafe?.user?.id;
          if (uid && w.ym) {
            // Do not override token-based userID if present
            if (!isToken) {
              w.ym(100644700, 'setUserID', String(uid));
            }
            w.ym(100644700, 'userParams', {
              tg_user_id: String(uid),
            });
          }
        } catch (_e) {
          // ignore metrica errors
          void 0;
        }
      } catch (_e) {
        // ignore yclid/referral parsing errors
        void 0;
      }

      moment.updateLocale('en', {
        months: [
          'Январь',
          'Февраль',
          'Март',
          'Апрель',
          'Май',
          'Июнь',
          'Июль',
          'Август',
          'Сентябрь',
          'Октябрь',
          'Ноябрь',
          'Декабрь',
        ],
        monthsShort: [
          'января',
          'февраля',
          'марта',
          'апреля',
          'мая',
          'июня',
          'июля',
          'августа',
          'сентября',
          'октября',
          'ноября',
          'декабря',
        ],
        relativeTime: {
          future: 'через %s',
          past: '%s назад',
          s: 'несколько секунд',
          m: 'минута',
          mm: '%d минут',
          h: 'час',
          hh: '%d часов',
          d: 'день',
          dd: '%d дней',
          M: 'месяц',
          MM: '%d месяцев',
          y: 'год',
          yy: '%d лет',
        },
      });
    })();
  }, []);

  return (
    <>
      <SWRConfig
        value={{
          refreshInterval: 0,
        }}
      >
        <TabbarProvider showNavigation={true}>
          <NotificationsProvider>
            <AuthProvider>
              <SseEngineContextProvider>
                <SkeletonTheme
                  baseColor="var(--skeleton-base-color)"
                  customHighlightBackground="var(--skeleton-highlight-color)"
                >
                  <Outlet />
                </SkeletonTheme>
              </SseEngineContextProvider>
            </AuthProvider>
          </NotificationsProvider>
        </TabbarProvider>
      </SWRConfig>
    </>
  );
}
